"use client";

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Search,
  Filter,
  RefreshCw,
  X,
  MonitorSmartphone,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";

/* ----------------------------- Types ----------------------------- */
type AuditRow = {
  _id: string;
  actor?: string;
  actorEmail?: string;
  action: string;
  entity: string;
  entityId: string;
  entityLabel?: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  diff?: string | null;
  ip?: string;
  userAgent?: string;
  createdAt: string;
};

type Car = { _id: string; name: string };
type Booking = { _id: string; customer?: string; car?: string; code?: string };

/* --------------------------- Constants --------------------------- */
const ACTIONS = [
  "create",
  "update",
  "delete",
  "approve",
  "reject",
  "status",
  "return",
  "login",
  "register",
] as const;

const PAGE_SIZE = 5;

/* ------------------------ Small utilities ------------------------ */
function fmtIp(ip?: string) {
  if (!ip) return "—";
  const v = ip.trim();
  if (v === "::1" || v === "127.0.0.1") return "—";
  return v;
}

function actionBadgeColor(action: string) {
  switch (action) {
    case "create":
      return "bg-emerald-100 text-emerald-800";
    case "update":
    case "status":
      return "bg-blue-100 text-blue-800";
    case "approve":
      return "bg-indigo-100 text-indigo-800";
    case "reject":
      return "bg-rose-100 text-rose-800";
    case "delete":
      return "bg-red-100 text-red-800";
    case "return":
      return "bg-teal-100 text-teal-800";
    case "login":
      return "bg-slate-100 text-slate-800";
    case "register":
      return "bg-violet-100 text-violet-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function getErrMsg(e: unknown): string {
  const maybe = e as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return maybe?.response?.data?.msg || maybe?.message || "Unexpected error";
}

// very light UA parser for display only
function parseDevice(ua?: string) {
  if (!ua) return "—";
  const s = ua.toLowerCase();
  const os = s.includes("windows")
    ? "Windows"
    : s.includes("mac os") || s.includes("macintosh")
    ? "macOS"
    : s.includes("linux")
    ? "Linux"
    : s.includes("android")
    ? "Android"
    : s.includes("iphone") || s.includes("ipad")
    ? "iOS"
    : "Device";

  const browser = s.includes("edg/")
    ? "Edge"
    : s.includes("chrome/")
    ? "Chrome"
    : s.includes("firefox/")
    ? "Firefox"
    : s.includes("safari/")
    ? "Safari"
    : "Browser";

  return `${os} • ${browser}`;
}

/* --------------------- Human-readable diff helper --------------------- */
function summarizeDiff(
  before: Record<string, unknown> = {},
  after: Record<string, unknown> = {}
) {
  const changes: Array<{ path: string; from: unknown; to: unknown }> = [];

  const isPrim = (
    v: unknown
  ): v is string | number | boolean | null | undefined =>
    v == null || ["string", "number", "boolean"].includes(typeof v);

  const short = (v: unknown) => {
    const s = String(v ?? "");
    return s.length > 120 ? s.slice(0, 117) + "…" : s;
  };

  const keysTop = new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ]);

  function walk(path: string, a: unknown, b: unknown) {
    if (a === b) return;
    if (isPrim(a) && isPrim(b)) {
      changes.push({ path, from: short(a), to: short(b) });
      return;
    }
    const aObj =
      a && typeof a === "object" ? (a as Record<string, unknown>) : {};
    const bObj =
      b && typeof b === "object" ? (b as Record<string, unknown>) : {};
    const sub = new Set([...Object.keys(aObj), ...Object.keys(bObj)]);
    if (sub.size === 0) {
      changes.push({ path, from: short(a), to: short(b) });
      return;
    }
    for (const k of sub) {
      walk(path ? `${path}.${k}` : k, aObj[k], bObj[k]);
    }
  }

  for (const k of keysTop) walk(k, before?.[k], after?.[k]);
  return changes;
}

/* ------------------------------ Page ----------------------------- */
const AuditPage = () => {
  // filters
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [limit, setLimit] = useState(100);

  // data
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);

  // search
  const [q, setQ] = useState("");

  // pagination
  const [page, setPage] = useState(1);

  // diff modal
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffRow, setDiffRow] = useState<AuditRow | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  // selection + bulk OTP modal
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // local caches for entity labels
  const [carMap, setCarMap] = useState<Record<string, string>>({});
  const [bookingMap, setBookingMap] = useState<Record<string, string>>({});

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { limit };
      if (entity.trim()) params.entity = entity.trim();
      if (action.trim()) params.action = action.trim();
      if (actor.trim()) params.actor = actor.trim();

      const [auditRes, carsRes, bookingsRes] = await Promise.all([
        api.get("/audit", { params }),
        api.get<Car[]>("/cars"),
        api.get<Booking[]>("/bookings"),
      ]);

      const payload: AuditRow[] = Array.isArray(auditRes.data)
        ? (auditRes.data as AuditRow[])
        : (auditRes.data?.data as AuditRow[]) ?? [];

      const cars = Array.isArray(carsRes.data) ? carsRes.data : [];
      const bookings = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];

      const cMap: Record<string, string> = {};
      cars.forEach((c) => (cMap[c._id] = c.name));

      const bMap: Record<string, string> = {};
      bookings.forEach((b) => {
        const label =
          b.code || (b.customer ? `Booking • ${b.customer}` : "Booking");
        bMap[b._id] = label;
      });

      const enriched = payload.map((r) => {
        if (r.entityLabel) return r;
        if (r.entity === "Car" && cMap[r.entityId]) {
          return { ...r, entityLabel: cMap[r.entityId] };
        }
        if (r.entity === "Booking" && bMap[r.entityId]) {
          return { ...r, entityLabel: bMap[r.entityId] };
        }
        return r;
      });

      setCarMap(cMap);
      setBookingMap(bMap);
      setRows(enriched);
      setSelected(new Set()); // clear selection on refresh
      setPage(1);
    } catch (e: unknown) {
      console.error(e);
      toast.error(getErrMsg(e) || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  // bulk: request OTP for selected ids
  const requestBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      setSendingCode(true);
      await api.post("/audit/bulk/delete/request", { ids });
      toast.success("Verification code sent to the admin email.");
      setOtpModalOpen(true);
    } catch (e: unknown) {
      toast.error(getErrMsg(e) || "Failed to send verification code.");
    } finally {
      setSendingCode(false);
    }
  };

  // bulk: confirm OTP and delete selected
  const confirmBulkDelete = async () => {
    if (!otpCode.trim()) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    try {
      setVerifying(true);
      await api.post("/audit/bulk/delete/confirm", { code: otpCode.trim() });
      toast.success("Selected audit logs deleted.");
      setOtpModalOpen(false);
      setOtpCode("");
      setSelected(new Set());
      await fetchLogs();
    } catch (e: unknown) {
      toast.error(getErrMsg(e) || "Invalid or expired code.");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      [
        r.action,
        r.entity,
        r.entityId,
        r.entityLabel || "",
        r.actor || "",
        r.actorEmail || "",
        r.ip || "",
        r.userAgent || "",
        r.createdAt,
        r.diff || "",
        JSON.stringify(r.before ?? {}),
        JSON.stringify(r.after ?? {}),
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [q, rows]);

  // pagination
  useEffect(() => setPage(1), [q, rows.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);
  const goTo = (p: number) => setPage(Math.max(1, Math.min(totalPages, p)));

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r._id));
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageRows.forEach((r) => next.delete(r._id));
      } else {
        pageRows.forEach((r) => next.add(r._id));
      }
      return next;
    });
  };

  // open diff
  const openDiff = (r: AuditRow) => {
    setDiffRow(r);
    setShowRaw(false);
    setDiffOpen(true);
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      {/* header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        {/* Left: search */}
        <div className="relative w-72">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Quick find…"
            className="w-full py-2 pl-8 pr-3 text-sm border rounded"
          />
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>

          {/* Clear Selected (OTP) */}
          <button
            onClick={requestBulkDelete}
            disabled={selected.size === 0 || sendingCode}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded ${
              selected.size === 0 || sendingCode
                ? "bg-red-300 text-white cursor-not-allowed"
                : "bg-red-600 text-white hover:bg-red-700"
            }`}
            title={
              selected.size === 0
                ? "Select rows to enable"
                : "Delete selected audit logs"
            }
          >
            {sendingCode
              ? "Sending code…"
              : `Clear Selected (${selected.size})`}
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="p-3 mb-4 border rounded bg-gray-50">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500">Entity</label>
            <input
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              placeholder='e.g. "Car" or "Booking"'
              className="px-2 py-1 text-sm border rounded w-44"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500">Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="px-2 py-1 text-sm border rounded w-44"
            >
              <option value="">Any</option>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500">Actor (Email)</label>
            <input
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="optional"
              className="px-2 py-1 text-sm border rounded w-44"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500">Limit</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-2 py-1 text-sm border rounded w-28"
            >
              {[50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60"
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>

          <span className="ml-auto text-xs text-gray-500">
            Showing {Math.min(PAGE_SIZE, Math.max(0, filtered.length - start))}{" "}
            of <b>{filtered.length}</b> (page {page} / {totalPages})
          </span>
        </div>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="text-xs text-blue-900 uppercase border-b border-gray-200 bg-blue-50">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={togglePage}
                  title={
                    allOnPageSelected
                      ? "Unselect this page"
                      : "Select this page"
                  }
                />
              </th>
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Actor</th>
              <th className="px-3 py-2">IP / Device</th>
              <th className="px-3 py-2">Diff</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-gray-400">
                  No audit entries
                </td>
              </tr>
            ) : (
              pageRows.map((r, i) => (
                <tr
                  key={`row-${r._id || r.createdAt || i}`}
                  className="border-b border-gray-100"
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r._id)}
                      onChange={() => toggleOne(r._id)}
                      title={selected.has(r._id) ? "Unmark" : "Mark"}
                    />
                  </td>

                  <td className="px-3 py-2">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>

                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold ${actionBadgeColor(
                        r.action
                      )}`}
                    >
                      {r.action}
                    </span>
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span className="font-medium">{r.entity}</span>
                      <span className="text-xs text-slate-500">
                        {r.entityLabel ||
                          (r.entity === "Car" && carMap[r.entityId]) ||
                          (r.entity === "Booking" && bookingMap[r.entityId]) ||
                          r.entityId}
                      </span>
                    </div>
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span className="text-xs">
                        {r.actorEmail || r.actor || "system"}
                      </span>
                      {!!r.actor && r.actorEmail && (
                        <span className="text-[11px] text-slate-400">
                          {r.actor}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">{fmtIp(r.ip)}</span>
                      <span className="text-slate-400">|</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                        <MonitorSmartphone className="h-3.5 w-3.5" />
                        {parseDevice(r.userAgent)}
                      </span>
                    </div>
                  </td>

                  <td className="px-3 py-2">
                    {r.before || r.after || r.diff ? (
                      <button
                        className="text-xs text-blue-700 hover:underline"
                        onClick={() => openDiff(r)}
                      >
                        {r.diff
                          ? r.diff.length > 80
                            ? r.diff.slice(0, 80) + "…"
                            : r.diff
                          : "view"}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex items-center justify-between px-2 mt-3 text-sm">
        <div className="text-xs text-gray-600">
          Showing{" "}
          <b>
            {filtered.length === 0
              ? 0
              : `${start + 1}–${Math.min(start + PAGE_SIZE, filtered.length)}`}
          </b>{" "}
          of <b>{filtered.length}</b>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
            className={`rounded border px-3 py-1 ${
              page === 1
                ? "cursor-not-allowed bg-gray-50 text-gray-400"
                : "border-blue-200 text-blue-700 hover:bg-blue-50"
            }`}
          >
            Prev
          </button>

          <span className="text-xs">
            Page <b>{page}</b> of <b>{totalPages}</b>
          </span>

          <button
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
            className={`rounded border px-3 py-1 ${
              page === totalPages
                ? "cursor-not-allowed bg-gray-50 text-gray-400"
                : "border-blue-200 text-blue-700 hover:bg-blue-50"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* OTP modal (bulk delete) */}
      {otpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md p-5 bg-white rounded shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-red-700">
                Confirm Deletion
              </h3>
              <button
                onClick={() => {
                  setOtpModalOpen(false);
                  setOtpCode("");
                }}
                className="text-gray-500 hover:text-black"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="mb-3 text-sm text-slate-600">
              We sent a 6-digit code to the admin email.
              <br />
              <span className="text-slate-700">Selected rows:</span>{" "}
              <b>{selected.size}</b>
            </p>

            <input
              inputMode="numeric"
              maxLength={6}
              placeholder="Enter 6-digit code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmBulkDelete();
              }}
              autoComplete="one-time-code"
              className="w-full px-3 py-2 text-sm border rounded outline-none focus:ring"
              autoFocus
            />

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                className="px-3 py-2 text-sm border rounded hover:bg-gray-50"
                onClick={() => {
                  setOtpModalOpen(false);
                  setOtpCode("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-60"
                onClick={confirmBulkDelete}
                disabled={verifying}
              >
                {verifying ? "Verifying…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* diff modal */}
      {diffOpen && diffRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-3xl p-4 bg-white rounded shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  {diffRow.entity} • {diffRow.entityLabel || diffRow.entityId}
                </h3>
                <div className="mt-1 text-xs text-slate-500">
                  {diffRow.actorEmail || diffRow.actor || "system"} —{" "}
                  {new Date(diffRow.createdAt).toLocaleString()} — IP{" "}
                  {fmtIp(diffRow.ip)} — {parseDevice(diffRow.userAgent)}
                </div>
              </div>
              <button
                onClick={() => setDiffOpen(false)}
                className="text-gray-500 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* actor/day summary */}
            {/* ...unchanged content omitted for brevity (your original) ... */}

            <div className="border rounded bg-slate-50">
              <div className="flex items-center justify-between px-3 py-2">
                <div className="text-sm font-semibold text-slate-700">
                  Changes
                </div>
                <button
                  onClick={() => setShowRaw((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                  title={showRaw ? "Hide raw JSON" : "Show raw JSON"}
                >
                  {showRaw ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  {showRaw ? "Hide raw JSON" : "Show raw JSON (advanced)"}
                </button>
              </div>

              <div className="max-h-[45vh] overflow-auto px-3 pb-3">
                {(() => {
                  const list = summarizeDiff(
                    diffRow?.before ?? {},
                    diffRow?.after ?? {}
                  );
                  if (list.length === 0)
                    return (
                      <div className="px-1 py-1 text-xs text-slate-500">
                        No field changes captured.
                      </div>
                    );
                  return (
                    <ul className="pr-1 text-xs">
                      {list.map((c, i) => (
                        <li key={i} className="py-0.5">
                          <span className="font-mono text-slate-600">
                            {c.path}
                          </span>
                          <span className="text-slate-400"> :</span>{" "}
                          <span className="line-through text-slate-500 decoration-rose-400/60">
                            {String(c.from)}
                          </span>{" "}
                          <span className="mx-1">→</span>
                          <span className="font-medium text-emerald-700">
                            {String(c.to)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>

            {showRaw && (
              <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                <div className="p-2 border rounded">
                  <div className="mb-2 text-sm font-medium text-slate-600">
                    Before (raw)
                  </div>
                  <pre className="max-h-[40vh] overflow-auto rounded bg-slate-50 p-2 text-xs">
                    {JSON.stringify(diffRow.before ?? {}, null, 2)}
                  </pre>
                </div>
                <div className="p-2 border rounded">
                  <div className="mb-2 text-sm font-medium text-slate-600">
                    After (raw)
                  </div>
                  <pre className="max-h-[40vh] overflow-auto rounded bg-slate-50 p-2 text-xs">
                    {JSON.stringify(diffRow.after ?? {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
